import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  checkPushPermission,
  flushPendingDeviceToken,
  getStoredDeviceToken,
  isPushSupported,
  registerPush,
  type PushPermission,
} from '@/lib/push'
import { useAuthStore } from '@/store/auth'

interface PushState {
  supported: boolean
  permission: PushPermission
  token: string | null
  error: string | null
}

/**
 * Registers for push on native platforms. Call once, from a component that
 * lives for the whole session (App root). Web builds are a no-op.
 *
 * Android is non-functional until `google-services.json` and the API 33+
 * POST_NOTIFICATIONS manifest permission are added — registration will fail
 * with a registrationError there, surfaced via `error`.
 */
export function usePush(options: { autoRegister?: boolean } = {}) {
  const { autoRegister = true } = options
  const navigate = useNavigate()
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const clientId = useAuthStore((s) => s.clientId)
  const teardownRef = useRef<null | (() => Promise<void>)>(null)

  const [state, setState] = useState<PushState>({
    supported: isPushSupported(),
    permission: isPushSupported() ? 'prompt' : 'unsupported',
    token: null,
    error: null,
  })

  const register = useCallback(async () => {
    if (!isPushSupported()) return

    // Registering twice duplicates listeners, so drop the previous set first.
    await teardownRef.current?.()
    teardownRef.current = null

    const { permission, teardown } = await registerPush({
      onToken: (token) => setState((s) => ({ ...s, token, error: null })),
      onError: (error) => setState((s) => ({ ...s, error })),
      onTapped: (action) => {
        const target = action.notification.data?.route
        if (typeof target === 'string' && target.startsWith('/')) navigate(target)
      },
    })

    teardownRef.current = teardown
    setState((s) => ({ ...s, permission }))
  }, [navigate])

  useEffect(() => {
    if (!isPushSupported()) return

    void (async () => {
      const permission = await checkPushPermission()
      const token = await getStoredDeviceToken()
      setState((s) => ({ ...s, permission, token }))
      if (autoRegister && permission === 'granted') await register()
    })()

    return () => {
      void teardownRef.current?.()
      teardownRef.current = null
    }
  }, [autoRegister, register])

  // The token usually arrives before there's a session to attach it to, so the
  // upload is retried whenever the user (or their client) resolves.
  useEffect(() => {
    if (!isPushSupported() || !userId) return
    void flushPendingDeviceToken()
  }, [userId, clientId])

  return { ...state, register }
}
